import { Typography } from '@mui/material';
import { AthletePreview, BenchmarkGrid, Metric } from '@strength/domain';
import {
  ExerciseBenchmark,
  KpiMetricResolution,
  MaxConversion,
  Workout,
  WorkoutExercise,
  WorkoutExerciseMetric,
} from '@strength/domain-workout';
import { I18nTranslator } from '@strength/i18n';
import { MdSubdirectoryArrowRight } from '@teamworksdev/icons';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Sheet } from '../../components/sheet';
import { CustomSlot } from '../../components/sheet/slots';
import { cn } from '../../components/utils';
import {
  hasPercentageIntensity,
  useBenchmarks,
} from '../../hooks/useBenchmarks';
import { useMetrics } from '../../hooks/useMetrics';
import { ActualWorkoutLayerMutationFns } from './actuals-mutations';
import { ActualsSetFilter } from './set-select';
import { getMetricHeaderSlots } from './slots/metrics-header-slots';
import { getSetSlotsForAthletes } from './slots/set-slots';
import { ActualSelectionData, ActualSlot } from './slots/types';
import {
  AthleteWorkoutExercises,
  ExerciseOption,
  MergedAthleteWorkout,
  MetricOption,
} from './types';
import { expandedExerciseOptions } from './utils';

export const ExerciseActuals = ({
  exerciseBenchmarks,
  conversions,
  athleteWorkouts,
  exerciseSelection,
  metricSelection,
  athleteSelection,
  setFilter,
  mutationFns,
  trainingMaxRatio,
  kpiBenchmarksByProfile,
  readOnly,
}: {
  exerciseBenchmarks: ExerciseBenchmark[];
  conversions: MaxConversion[];
  athleteWorkouts: MergedAthleteWorkout[];
  exerciseSelection: ExerciseOption[];
  metricSelection: MetricOption[];
  athleteSelection: AthletePreview[];
  setFilter: ActualsSetFilter;
  mutationFns: ActualWorkoutLayerMutationFns;
  trainingMaxRatio: number;
  kpiBenchmarksByProfile: Record<string, Record<string, KpiMetricResolution>>;
  // Actuals Log revoked — every actuals cell renders read-only (SC-4378).
  readOnly: boolean;
}) => {
  const expandedExercises = useMemo(
    () => expandedExerciseOptions(exerciseSelection),
    [exerciseSelection],
  );

  const allExercises = expandedExercises
    .map(({ workoutExerciseId, libraryExerciseId, isAlternate }) => {
      return getAthleteWorkoutExercisesForExercise(
        athleteWorkouts,
        workoutExerciseId,
        libraryExerciseId,
        isAlternate,
        exerciseBenchmarks,
        conversions,
        trainingMaxRatio,
      );
    })
    .flat();

  // filter out metrics that are not in the metricSelection
  const filterForMetrics = (metrics: WorkoutExerciseMetric[]) => {
    return metrics.filter((m) =>
      metricSelection.some((m2) => m2.id === m.metricId),
    );
  };

  // Find the most metrics across a single exercise. We use this to pad the metrics so they line up neatly
  const maxMetricCount = Math.max(
    ...allExercises.map(
      (ex) => filterForMetrics(ex.originalExercise?.metrics || []).length || 0,
    ),
  );
  return (
    <div className="flex min-w-0 w-full flex-col gap-4">
      {expandedExercises.map(
        ({ workoutExerciseId, libraryExerciseId, name, isAlternate }) => {
          const athleteWorkoutExercises = getAthleteWorkoutExercisesForExercise(
            athleteWorkouts,
            workoutExerciseId,
            libraryExerciseId,
            isAlternate,
            exerciseBenchmarks,
            conversions,
            trainingMaxRatio,
          );

          if (
            !athleteWorkoutExercises.find(
              (w) => filterForMetrics(w.workoutExercise.metrics).length > 0,
            )
          ) {
            // Don't display exercises w/o metrics/sets
            return null;
          }

          return (
            <ExerciseActualsSheet
              key={workoutExerciseId}
              athleteWorkoutExercises={athleteWorkoutExercises}
              exerciseName={name}
              libraryExerciseId={libraryExerciseId}
              mutationFns={mutationFns}
              athleteSelection={athleteSelection}
              metricSelection={metricSelection}
              setFilter={setFilter}
              maxMetricCount={maxMetricCount}
              isAlternate={isAlternate}
              exerciseBenchmarks={exerciseBenchmarks}
              conversions={conversions}
              trainingMaxRatio={trainingMaxRatio}
              kpiBenchmarksByProfile={kpiBenchmarksByProfile}
              readOnly={readOnly}
            />
          );
        },
      )}
    </div>
  );
};

const ExerciseActualsSheet = ({
  exerciseName,
  athleteWorkoutExercises,
  libraryExerciseId,
  mutationFns,
  athleteSelection,
  setFilter,
  metricSelection,
  maxMetricCount,
  isAlternate,
  exerciseBenchmarks,
  conversions,
  trainingMaxRatio,
  kpiBenchmarksByProfile,
  readOnly,
}: {
  exerciseName: string;
  athleteWorkoutExercises: AthleteWorkoutExercises[];
  libraryExerciseId: string;
  mutationFns: ActualWorkoutLayerMutationFns;
  athleteSelection: AthletePreview[];
  setFilter: ActualsSetFilter;
  metricSelection: MetricOption[];
  maxMetricCount: number;
  isAlternate: boolean;
  exerciseBenchmarks: ExerciseBenchmark[];
  conversions: MaxConversion[];
  trainingMaxRatio: number;
  kpiBenchmarksByProfile: Record<string, Record<string, KpiMetricResolution>>;
  readOnly: boolean;
}) => {
  const { getMetric, getUnitsForMetric } = useMetrics();
  const { defaultBenchmarkGridId, getBenchmarkGrid } = useBenchmarks();
  const { t } = useTranslation();

  // Calculate metrics for grid template
  const athleteExerciseMetrics = getMetricsForExerciseDisplay(
    athleteWorkoutExercises,
    metricSelection,
  );

  // Build custom grid template: 180px (athlete) + 80px (set) + 88px * prescribed + 116px * actuals + 1fr (flexible)
  const customGridTemplate = `180px 80px ${'88px '.repeat(
    athleteExerciseMetrics.length,
  )}${'116px '.repeat(athleteExerciseMetrics.length)}1fr`;

  return (
    <Sheet<ActualSelectionData>
      {...getSlotsForExerciseActuals(
        exerciseName,
        athleteWorkoutExercises,
        mutationFns,
        athleteSelection,
        setFilter,
        maxMetricCount,
        getMetric,
        getUnitsForMetric,
        t,
        isAlternate,
        athleteExerciseMetrics,
        exerciseBenchmarks,
        conversions,
        trainingMaxRatio,
        defaultBenchmarkGridId,
        getBenchmarkGrid,
        libraryExerciseId,
        kpiBenchmarksByProfile,
        readOnly,
      )}
      className={cn(
        'min-w-full w-full overflow-auto overscroll-auto max-h-[800px]',
        isAlternate ? '-mt-4' : '',
      )}
      gridTemplateColumns={customGridTemplate}
      onUndo={() => {}}
      onRedo={() => {}}
      onCopy={() => {}}
      onPaste={() => {}}
      onBulkClear={() => {}}
      getContextMenuContent={() => null}
      onDragEnd={() => {}}
    />
  );
};

const getSlotsForExerciseActuals = (
  exerciseName: string,
  athleteWorkoutExercises: AthleteWorkoutExercises[],
  mutationFns: ActualWorkoutLayerMutationFns,
  athleteSelection: AthletePreview[],
  setFilter: ActualsSetFilter,
  maxMetricCount: number,
  getMetric: (id: string) => Metric,
  getUnitsForMetric: (id: string, forActuals?: boolean) => string[],
  t: I18nTranslator,
  isAlternate: boolean,
  athleteExerciseMetrics: WorkoutExerciseMetric[],
  exerciseBenchmarks: ExerciseBenchmark[],
  conversions: MaxConversion[],
  trainingMaxRatio: number,
  defaultBenchmarkGridId: string,
  getBenchmarkGrid: (benchmarkGridId: string) => BenchmarkGrid | null,
  libraryExerciseId: string | undefined,
  kpiBenchmarksByProfile: Record<string, Record<string, KpiMetricResolution>>,
  readOnly: boolean,
): {
  slots: ActualSlot[];
  rowCount: number;
  columnCount: number;
} => {
  // athlete name + sets metric + (metric * prescribed + actuals) + spacer column
  const columnCount = 2 + athleteExerciseMetrics.length * 2 + 1;
  let rowCount = 1; // at least one row for Metrics header row
  athleteWorkoutExercises.forEach((w) =>
    w.workoutExercise.sets.forEach(() => rowCount++),
  );

  let currentX = 0;
  let currentY = 0;
  const slots = [];

  // Exercise Name
  slots.push(
    new CustomSlot<ActualSelectionData>(
      { x: currentX, y: currentY++ },
      columnCount,
      1,
      false,
      () => (
        <div className="sticky left-0 p-2">
          {isAlternate && <MdSubdirectoryArrowRight className="mr-1" />}
          {exerciseName}
        </div>
      ),
      {
        className: 'font-semibold',
      },
    ),
  );

  // empty slot for athlete name column (180px)
  slots.push(
    new CustomSlot<ActualSelectionData>(
      { x: currentX, y: currentY },
      1,
      1,
      false,
      () => <></>,
      {
        className:
          'sticky left-0 bg-white w-full w-[180px] border-r-0 pointer-events-none',
      },
    ),
  );
  currentX += 1;

  // empty slot for set number column
  slots.push(
    new CustomSlot<ActualSelectionData>(
      { x: currentX, y: currentY },
      1,
      1,
      false,
      () => <></>,
      {
        className:
          'sticky left-[180px] bg-white w-full w-[80px] border-l-0 pointer-events-none',
      },
    ),
  );
  currentX += 1;

  // Prescribed header
  slots.push(
    new CustomSlot<ActualSelectionData>(
      { x: currentX, y: currentY },
      athleteExerciseMetrics.length,
      1,
      false,
      () => (
        <Typography variant="subtitle1Medium">{t('prescribed')}</Typography>
      ),
      {
        className: 'p-2',
        backgroundClassName: 'bg-gray-50',
      },
    ),
  );
  currentX += athleteExerciseMetrics.length;

  // Actuals header
  slots.push(
    new CustomSlot<ActualSelectionData>(
      { x: currentX, y: currentY },
      athleteExerciseMetrics.length,
      1,
      false,
      () => <Typography variant="subtitle1Medium">{t('actuals')}</Typography>,
      {
        className: 'p-2',
      },
    ),
  );
  currentX += athleteExerciseMetrics.length;

  // blank header to fill remaining space
  slots.push(
    new CustomSlot<ActualSelectionData>(
      { x: columnCount - 1, y: currentY },
      1,
      1,
      true,
      () => <></>,
      {
        className: 'bg-transparent pointer-events-none min-w-0',
      },
    ),
  );

  // Get header row for set/metrics
  currentY++;
  const intensityMetric = athleteExerciseMetrics.find(
    (m) => m.metricType === 'intensity',
  );
  const percentageIntensity = intensityMetric
    ? hasPercentageIntensity(
        intensityMetric,
        athleteWorkoutExercises.flatMap((awe) =>
          awe.workoutExercise.sets.flatMap((set) => set.metrics),
        ),
      )
    : false;
  const metricsHeaderSlots = getMetricHeaderSlots(
    athleteExerciseMetrics,
    0,
    currentY,
    t,
    getBenchmarkGrid,
    defaultBenchmarkGridId,
    libraryExerciseId,
    percentageIntensity,
  );
  slots.push(...metricsHeaderSlots);

  currentY++;

  // Get slots for each set
  const { slots: athleteSetSlots, rowCount: athleteSetsHeight } =
    getSetSlotsForAthletes(
      athleteWorkoutExercises,
      athleteExerciseMetrics,
      athleteSelection,
      mutationFns,
      setFilter,
      currentY,
      getMetric,
      getUnitsForMetric,
      exerciseBenchmarks,
      conversions,
      trainingMaxRatio,
      defaultBenchmarkGridId,
      kpiBenchmarksByProfile,
      readOnly,
    );
  slots.push(...athleteSetSlots);
  currentY += athleteSetsHeight;

  // The spacer is now handled by the blank headers and blank metric values
  // No need for additional spacer since we've handled it in headers and data rows

  return {
    slots,
    rowCount: currentY,
    columnCount,
  };
};

/**
 * Returns the exercises for a given exercise id for each athlete.
 */
const getAthleteWorkoutExercisesForExercise = (
  athleteWorkouts: MergedAthleteWorkout[],
  workoutExerciseId: string,
  libraryExerciseId: string,
  isAlternate: boolean,
  exerciseBenchmarks: ExerciseBenchmark[],
  conversions: MaxConversion[],
  trainingMaxRatio: number,
): AthleteWorkoutExercises[] => {
  return athleteWorkouts.flatMap(({ context, workout, originalWorkout }) => {
    const workoutExercise = getWorkoutExerciseFromWorkoutForExercise(
      workout,
      workoutExerciseId,
    );
    const originalExercise = getWorkoutExerciseFromWorkoutForExercise(
      originalWorkout,
      workoutExerciseId,
      true,
    );

    if (!workoutExercise || !originalExercise) {
      return [];
    }

    return [
      {
        context,
        workoutExercise,
        originalExercise,
        isAlternate,
        exerciseBenchmark: exerciseBenchmarks.find(
          (m) =>
            m.libraryExerciseId === libraryExerciseId &&
            m.profileId === context.profileId,
        ),
        conversion: conversions.find(
          (c) => c.toLibraryExerciseId === libraryExerciseId,
        ),
        trainingMaxRatio,
      },
    ];
  });
};

/**
 * Returns the metrics used across all athletes for each exercise
 * @param athleteWorkoutExercises
 * @returns
 */
export const getMetricsForExerciseDisplay = (
  athleteWorkoutExercises: AthleteWorkoutExercises[],
  metricSelection: MetricOption[],
): WorkoutExerciseMetric[] => {
  const athleteExerciseMetrics: WorkoutExerciseMetric[] = [];
  athleteWorkoutExercises.forEach((w) =>
    w.workoutExercise.metrics.forEach((m) => {
      const matchingExerciseMetric = athleteExerciseMetrics.find(
        (a) => a.metricId === m.metricId,
      );
      if (
        !matchingExerciseMetric &&
        metricSelection.some((m2) => m2.id === m.metricId)
      ) {
        athleteExerciseMetrics.push(m);
      }
    }),
  );
  return athleteExerciseMetrics;
};

export const getWorkoutExerciseFromWorkoutForExercise = (
  workout: Workout,
  workoutExerciseId: string,
  ignoreSelected = false,
): WorkoutExercise | undefined => {
  for (const section of workout.sections) {
    for (const entry of section.entries) {
      if (
        entry.__type === 'WorkoutExercise' &&
        entry.workoutExerciseId === workoutExerciseId
      ) {
        return entry;
      } else if (entry.__type === 'WorkoutSuperset') {
        const foundExercise = entry.exercises.find(
          (e) => e.exercise.workoutExerciseId === workoutExerciseId,
        );
        if (foundExercise) {
          return foundExercise.exercise;
        }
      } else if (entry.__type === 'WorkoutAlternateGroup') {
        const foundExercise = entry.exercises.find(
          (e) => e.exercise.workoutExerciseId === workoutExerciseId,
        );
        // If we are finding the original exercise, we just want to find the matching
        // one, regardless if it's the currently selected one in the alternate group
        if (
          foundExercise &&
          (ignoreSelected || workoutExerciseId === entry.selectedExerciseId)
        ) {
          return foundExercise.exercise;
        }
      }
    }
  }
  return undefined;
};
